import React, { useState, useEffect } from 'react';
import { 
  FiEye,        // View icon
  FiMessageCircle, // Message/Contact icon
  FiRefreshCw   // Refresh icon
} from 'react-icons/fi';
import { IoSearchOutline } from 'react-icons/io5';
import { FaWhatsapp } from 'react-icons/fa';

const StudentActionsTable = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});
  const [contactCounts, setContactCounts] = useState({
    callCount: 0,
    whatsappCount: 0,
    totalCount: 0
  });
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ 
    key: 'timestamp', 
    direction: 'desc' 
  });

  // Fetch contact action counts
  const fetchContactCounts = async (signal) => {
    try {
      const response = await fetch('https://apissc.sainikschoolcadet.com/api/contact-actions/count', { signal });
      const data = await response.json();
      if (data.success) {
        setContactCounts(data.counts);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch contact counts:', err);
    }
  };

  // Fetch student actions
  const fetchStudentActions = async (signal) => {
    setLoading(true);
    try {
      const response = await fetch('https://apissc.sainikschoolcadet.com/api/contact-actions/', { signal });
      const data = await response.json();
      
      // Fetch student details for each unique student ID
      const uniqueStudentIds = [...new Set(data.actions.map(action => action.s_id))];
      const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
        try {
          const studentResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`, { signal });
          const studentData = await studentResponse.json();
          return {
            [studentId]: {
              name: studentData.f_name,
              phone_number: studentData.phonenumber,
              email: studentData.email,
              class_entry: studentData.c_entry,
              school: studentData.c_school,
              gender: studentData.gender,
              state: studentData.state
            }
          };
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          console.error(`Failed to fetch student details for ID ${studentId}:`, err);
          return { [studentId]: { name: 'Unknown Student', phone_number: 'N/A' } };
        }
      });

      // Resolve student details
      const studentDetailsArray = await Promise.all(studentDetailsPromises);

      if (signal && signal.aborted) return;

      const studentDetailsMap = studentDetailsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {});

      setStudentDetails(studentDetailsMap);
      setActions(data.actions);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch student actions:', err);
      setError('Failed to fetch student actions');
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const controller = new AbortController();
    fetchContactCounts(controller.signal);
    fetchStudentActions(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  // Sorting function
  const sortedActions = [...actions].sort((a, b) => {
    if (sortConfig.key === 'timestamp') {
      return sortConfig.direction === 'asc'
        ? new Date(a.timestamp) - new Date(b.timestamp)
        : new Date(b.timestamp) - new Date(a.timestamp);
    }
    return 0;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' 
        ? 'asc' 
        : 'desc'
    }));
  };

  const handleViewStudent = (studentId) => {
    window.open(`/view-student/${studentId}`, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppContact = (phoneNumber) => {
    // Ensure phone number starts with +91
    const formattedNumber = phoneNumber.startsWith('+91') 
      ? phoneNumber 
      : `+91${phoneNumber.replace(/[^\d]/g, '')}`;
    
    // Open WhatsApp web with the formatted number
    window.open(`https://wa.me/${formattedNumber}`, '_blank', 'noopener,noreferrer');
  };

  // Filter actions based on search query and action type
  const filteredActions = sortedActions.filter(action => {
    const student = studentDetails[action.s_id] || {};
    const matchesSearch = student.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || action.action_type === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Student Actions</h2>
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => {
              fetchContactCounts();
              fetchStudentActions();
            }}
            className="text-gray-600 hover:text-blue-600 transition-colors"
            title="Refresh"
          >
            <FiRefreshCw size={20} />
          </button>
         
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Total Contacts</p>
          <p className="text-2xl font-bold text-blue-600">{contactCounts.totalCount}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">WhatsApp Actions</p>
          <p className="text-2xl font-bold text-green-600">
            {contactCounts.whatsappCount} 
            <span className="text-sm text-gray-500">
              {` (${Math.round((contactCounts.whatsappCount / contactCounts.totalCount) * 100) || 0}%)`}
            </span>
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Call Actions</p>
          <p className="text-2xl font-bold text-purple-600">
            {contactCounts.callCount}
            <span className="text-sm text-gray-500">
              {` (${Math.round((contactCounts.callCount / contactCounts.totalCount) * 100) || 0}%)`}
            </span>
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Actions</option>
            <option value="call">Call</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <div className="relative">
            <input 
              type="text"
              placeholder="Search Student"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <IoSearchOutline className="h-5 w-5 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.NO.</th>
                
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action Type
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center">
                    Timestamp
                    {sortConfig.key === 'timestamp' && (
                      <span className="ml-2">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredActions.map((action, index) => {
                const student = studentDetails[action.s_id] || {};
                return (
                  <tr key={action.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{student.name || 'Unknown Student'}</div>
                          <div className="text-sm text-gray-500">{student.phone_number || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          action.action_type === 'whatsapp'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {action.action_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(action.timestamp)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleViewStudent(action.s_id)}
                          className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                          title="View Student"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
  onClick={() => handleWhatsAppContact(student.phone_number)}
  className="text-green-500 hover:text-green-700 p-2 rounded-full hover:bg-green-50"
  title="Contact on WhatsApp"
>
  <FaWhatsapp size={18} />
</button>

                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* No Data */}
          {filteredActions.length === 0 && (
            <div className="text-center py-10 text-gray-500">No student actions found.</div>
          )}
        </div>
      )}
      
      {/* Pagination */}
      {filteredActions.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-700">
              Showing 1 to {filteredActions.length} of {filteredActions.length} results
            </p>
          </div>
          <div className="flex space-x-1">
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &lt;
            </button>
            <button className="px-3 py-1 rounded border border-blue-500 bg-blue-50 text-blue-600">
              1
            </button>
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentActionsTable;